import { useState, useRef, useEffect } from 'react'
import { Filter, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDashboardStore } from '../stores/dashboardStore'
import { useEmployees } from '../hooks/useEmployees'
import { useOrderTemplates } from '../hooks/useOrderTemplates'
import { useGroupedEmployees } from '../hooks/useGroups'

type FilterOption =
  | { column: 'employee' | 'template'; label: string; kind: 'dropdown' }
  | { column: 'customerName' | 'deliveryName' | 'customerId'; label: string; kind: 'text' }
  | { column: 'yearRange'; label: string; kind: 'yearRange' }

const FILTER_OPTIONS: FilterOption[] = [
  { column: 'yearRange', label: 'År', kind: 'yearRange' },
  { column: 'employee', label: 'Ansatt', kind: 'dropdown' },
  { column: 'template', label: 'Ordremal', kind: 'dropdown' },
  { column: 'customerName', label: 'Kunde faktura', kind: 'text' },
  { column: 'deliveryName', label: 'Kunde levering', kind: 'text' },
  { column: 'customerId', label: 'Kundenr faktura', kind: 'text' },
]

export function AddFilterButton() {
  const [open, setOpen] = useState(false)
  const [activeOption, setActiveOption] = useState<FilterOption | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const employeeFilter = useDashboardStore((s) => s.employeeFilter)
  const templateFilter = useDashboardStore((s) => s.templateFilter)
  const customerNameFilter = useDashboardStore((s) => s.customerNameFilter)
  const deliveryNameFilter = useDashboardStore((s) => s.deliveryNameFilter)
  const customerIdFilter = useDashboardStore((s) => s.customerIdFilter)
  const orderDateFrom = useDashboardStore((s) => s.orderDateFrom)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setActiveOption(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const isActive = (opt: FilterOption): boolean => {
    if (opt.column === 'yearRange') return !!orderDateFrom
    if (opt.column === 'employee') return employeeFilter.length > 0
    if (opt.column === 'template') return templateFilter.length > 0
    if (opt.column === 'customerName') return customerNameFilter.length > 0
    if (opt.column === 'deliveryName') return deliveryNameFilter.length > 0
    if (opt.column === 'customerId') return customerIdFilter.length > 0
    return false
  }

  const available = FILTER_OPTIONS.filter((f) => !isActive(f))

  if (available.length === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          setOpen(!open)
          setActiveOption(null)
        }}
        className="flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded-md border border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors"
      >
        <Filter size={12} />
        Hurtig filter
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
          {!activeOption ? (
            <div className="py-1">
              {available.map((opt) => (
                <button
                  key={opt.column}
                  onClick={() => setActiveOption(opt)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-[13px] text-left hover:bg-gray-50 text-gray-700"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : activeOption.kind === 'yearRange' ? (
            <YearRangePanel onClose={() => { setOpen(false); setActiveOption(null) }} />
          ) : activeOption.kind === 'dropdown' ? (
            activeOption.column === 'employee' ? (
              <EmployeeFilterPanel />
            ) : (
              <TemplateFilterPanel />
            )
          ) : (
            <TextFilterPanel column={activeOption.column} onClose={() => { setOpen(false); setActiveOption(null) }} />
          )}
        </div>
      )}
    </div>
  )
}

function YearRangePanel({ onClose }: { onClose: () => void }) {
  const setDateRange = useDashboardStore((s) => s.setDateRange)
  const orderDateFrom = useDashboardStore((s) => s.orderDateFrom)

  // Reading the clock during render is impure; capture it once when the panel
  // opens instead. The panel is short-lived, so a mount-time "now" is enough
  // to tell which year preset the active filter came from.
  const [now] = useState(() => Date.now())

  const currentYears = orderDateFrom
    ? Math.round((now - new Date(orderDateFrom).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null

  const setYears = (years: number) => {
    const from = new Date()
    from.setFullYear(from.getFullYear() - years)
    setDateRange('orderDate', from.toISOString().split('T')[0], null)
    onClose()
  }

  return (
    <div className="py-1">
      {[1, 2, 3, 4, 5].map((y) => (
        <button
          key={y}
          onClick={() => setYears(y)}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50',
            currentYears === y ? 'text-emerald-600 font-medium' : 'text-gray-700'
          )}
        >
          {currentYears === y && <Check size={12} />}
          {y} år
        </button>
      ))}
    </div>
  )
}

function TextFilterPanel({
  column,
  onClose,
}: {
  column: 'customerName' | 'deliveryName' | 'customerId'
  onClose: () => void
}) {
  const setTextFilter = useDashboardStore((s) => s.setTextFilter)
  const value = useDashboardStore((s) => {
    switch (column) {
      case 'customerName': return s.customerNameFilter
      case 'deliveryName': return s.deliveryNameFilter
      case 'customerId': return s.customerIdFilter
    }
  })

  return (
    <div className="px-3 py-2 space-y-1.5">
      <input
        type="text"
        autoFocus
        placeholder="Skriv for å filtrere..."
        value={value}
        onChange={(e) => setTextFilter(column, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
          if (e.key === 'Enter' && value) onClose()
        }}
        className="w-full px-2 py-1.5 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400"
      />
    </div>
  )
}

function EmployeeFilterPanel() {
  const { data: employees = [] } = useEmployees()
  const groupedEmployees = useGroupedEmployees(employees)
  const employeeFilter = useDashboardStore((s) => s.employeeFilter)
  const setEmployeeFilter = useDashboardStore((s) => s.setEmployeeFilter)

  const allSelected = employeeFilter.length === 0

  const onToggle = (val: number) => {
    setEmployeeFilter(
      employeeFilter.includes(val)
        ? employeeFilter.filter((s) => s !== val)
        : [...employeeFilter, val]
    )
  }

  if (groupedEmployees) {
    return (
      <div className="max-h-64 overflow-y-auto py-1">
        <button
          onClick={() => setEmployeeFilter([])}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
        >
          <div
            className={cn(
              'w-3.5 h-3.5 rounded border flex items-center justify-center',
              allSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
            )}
          >
            {allSelected && <Check size={10} className="text-white" />}
          </div>
          Alle
        </button>
        <div className="h-px bg-gray-100 my-0.5" />
        {groupedEmployees.map((group, i) => {
          const prev = i > 0 ? groupedEmployees[i - 1] : null
          const showSectionHeader = !prev || prev.groupType !== group.groupType
          return (
            <div key={group.groupName}>
              {showSectionHeader && group.groupType === 'department' && (
                <div className="px-3 pt-2 pb-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-300">Avdelinger</div>
              )}
              {showSectionHeader && group.groupType === 'employee' && (
                <>
                  <div className="h-px bg-gray-100 my-1" />
                  <div className="px-3 pt-1 pb-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-300">Grupper</div>
                </>
              )}
              {showSectionHeader && group.groupType === 'ungrouped' && (
                <div className="h-px bg-gray-100 my-1" />
              )}
              <button
                onClick={() => {
                  const groupIds = group.items.map((i) => i.value)
                  const allInGroupSelected = groupIds.every((id) => employeeFilter.includes(id))
                  if (allInGroupSelected) {
                    setEmployeeFilter(employeeFilter.filter((id) => !groupIds.includes(id)))
                  } else {
                    setEmployeeFilter([...new Set([...employeeFilter, ...groupIds])])
                  }
                }}
                className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 flex items-center gap-1.5 w-full text-left hover:bg-gray-50 transition-colors"
              >
                {group.groupColor && (
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: group.groupColor }}
                  />
                )}
                {group.groupName}
                {group.items.every((i) => employeeFilter.includes(i.value)) && (
                  <Check size={10} className="text-emerald-500 ml-auto" />
                )}
              </button>
              {group.items.map((opt) => {
                const isSelected = employeeFilter.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    onClick={() => onToggle(opt.value)}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50 pl-5"
                  >
                    <div
                      className={cn(
                        'w-3.5 h-3.5 rounded border flex items-center justify-center',
                        isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                      )}
                    >
                      {isSelected && <Check size={10} className="text-white" />}
                    </div>
                    {opt.label}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    )
  }

  // Fallback: flat list
  return (
    <div className="max-h-64 overflow-y-auto py-1">
      <button
        onClick={() => setEmployeeFilter([])}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
      >
        <div
          className={cn(
            'w-3.5 h-3.5 rounded border flex items-center justify-center',
            allSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
          )}
        >
          {allSelected && <Check size={10} className="text-white" />}
        </div>
        Alle
      </button>
      <div className="h-px bg-gray-100 my-0.5" />
      {employees.map((e) => {
        const isSelected = employeeFilter.includes(e.employee_id)
        return (
          <button
            key={e.employee_id}
            onClick={() => onToggle(e.employee_id)}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
          >
            <div
              className={cn(
                'w-3.5 h-3.5 rounded border flex items-center justify-center',
                isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
              )}
            >
              {isSelected && <Check size={10} className="text-white" />}
            </div>
            {e.full_name}
          </button>
        )
      })}
    </div>
  )
}

function TemplateFilterPanel() {
  const { data: templates = [] } = useOrderTemplates()
  const templateFilter = useDashboardStore((s) => s.templateFilter)
  const setTemplateFilter = useDashboardStore((s) => s.setTemplateFilter)

  const allSelected = templateFilter.length === 0

  const onToggle = (val: number) => {
    setTemplateFilter(
      templateFilter.includes(val)
        ? templateFilter.filter((s) => s !== val)
        : [...templateFilter, val]
    )
  }

  return (
    <div className="max-h-64 overflow-y-auto py-1">
      <button
        onClick={() => setTemplateFilter([])}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
      >
        <div
          className={cn(
            'w-3.5 h-3.5 rounded border flex items-center justify-center',
            allSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
          )}
        >
          {allSelected && <Check size={10} className="text-white" />}
        </div>
        Alle
      </button>
      <div className="h-px bg-gray-100 my-0.5" />
      {templates.map((t) => {
        const isSelected = templateFilter.includes(t.order_template_id)
        return (
          <button
            key={t.order_template_id}
            onClick={() => onToggle(t.order_template_id)}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
          >
            <div
              className={cn(
                'w-3.5 h-3.5 rounded border flex items-center justify-center',
                isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
              )}
            >
              {isSelected && <Check size={10} className="text-white" />}
            </div>
            {t.name}
          </button>
        )
      })}
    </div>
  )
}
